// GuideView.tsx — Guía de uso de GrindLog + FAQ
// Para agregar al router: tab === 'guide' → <GuideView />
// Agregar en Header: botón "?" o "Guía" que setTab('guide')

import React, { useState } from 'react';
import { useT } from '../lib/i18n';

// ── Imágenes — ponelas en /public/guide/ con estos nombres ───────────────────
const IMG = {
  lobby:    '/guide/01-ggpoker-lobby.png',
  history:  '/guide/03-pokercraft-history.png',
  download: '/guide/04-download-buttons.png',
  files:    '/guide/05-downloaded-files.png',
  dropzone: '/guide/07-grindlog-import.png',
  result:   '/guide/08-import-result.png',
  stats:    '/guide/09-stats-view.png',
  ai:       '/guide/10-ai-analysis.png',
};

// ── Step component ────────────────────────────────────────────────────────────
interface StepProps {
  number: number;
  title: string;
  children: React.ReactNode;
  img?: string;
  imgAlt?: string;
  tip?: string;
  warning?: string;
  zoomHint: string;
}

function Step({ number, title, children, img, imgAlt, tip, warning, zoomHint }: StepProps) {
  return (
    <div className="guide-step">
      <div className="guide-step-header">
        <div className="guide-step-num">{number}</div>
        <h3 className="guide-step-title">{title}</h3>
      </div>
      <div className="guide-step-body">
        <div className="guide-step-text">
          {children}
          {tip && (
            <div className="guide-tip">
              <span className="guide-tip-icon">💡</span>
              <span>{tip}</span>
            </div>
          )}
          {warning && (
            <div className="guide-warning">
              <span className="guide-tip-icon">⚠️</span>
              <span>{warning}</span>
            </div>
          )}
        </div>
        {img && (
          <div className="guide-step-img-wrap">
            <img
              src={img}
              alt={imgAlt || title}
              className="guide-step-img"
              onClick={e => (e.currentTarget as HTMLImageElement).classList.toggle('guide-img-zoom')}
            />
            <p className="guide-img-caption">{zoomHint}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── FAQ item ──────────────────────────────────────────────────────────────────
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-open' : ''}`}>
      <button className="faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <span className="faq-arrow">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="faq-a"><p>{a}</p></div>}
    </div>
  );
}

// ── Stats row ─────────────────────────────────────────────────────────────────
function StatRow({ stat, range, desc }: { stat: string; range: string; desc: string }) {
  return (
    <div className="sgt-row">
      <span className="sgt-stat">{stat}</span>
      <span className="sgt-range opt">{range}</span>
      <span>{desc}</span>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
type GuideSection = 'guide' | 'faq' | 'stats';

interface GuideViewProps {
  initialSection?: GuideSection;
}

export function GuideView({ initialSection = 'guide' }: GuideViewProps) {
  const { t } = useT();
  const [section, setSection] = useState<GuideSection>(initialSection);
  const zh = t('guide.zoomHint');

  return (
    <div className="guide-view">

      {/* Header */}
      <div className="guide-header">
        <div className="guide-header-text">
          <h2 className="guide-title">{t('guide.title')}</h2>
          <p className="guide-subtitle">{t('guide.subtitle')}</p>
        </div>
        <div className="guide-tabs">
          <button className={`guide-tab ${section === 'guide' ? 'active' : ''}`} onClick={() => setSection('guide')}>
            {t('guide.tabGuide')}
          </button>
          <button className={`guide-tab ${section === 'stats' ? 'active' : ''}`} onClick={() => setSection('stats')}>
            {t('guide.tabStats')}
          </button>
          <button className={`guide-tab ${section === 'faq' ? 'active' : ''}`} onClick={() => setSection('faq')}>
            {t('guide.tabFaq')}
          </button>
        </div>
      </div>

      {/* ── GUÍA PASO A PASO ─────────────────────────────────────────── */}
      {section === 'guide' && (
        <div className="guide-steps">
          <div className="guide-section-intro">
            <p>{t('guide.introText')}</p>
          </div>

          <Step number={1} title={t('guide.step1Title')} img={IMG.lobby} imgAlt={t('guide.step1ImgAlt')} tip={t('guide.step1Tip')} zoomHint={zh}>
            <p>{t('guide.step1Body')}</p>
          </Step>

          <Step number={2} title={t('guide.step2Title')} img={IMG.history} imgAlt={t('guide.step2ImgAlt')} tip={t('guide.step2Tip')} zoomHint={zh}>
            <p>{t('guide.step2Body')}</p>
            <ul className="guide-list">
              <li><strong>Download Game Summaries</strong> — {t('guide.step2Li1').replace('Download Game Summaries — ', '')}</li>
              <li><strong>Download Game Histories</strong> — {t('guide.step2Li2').replace('Download Game Histories — ', '')}</li>
            </ul>
          </Step>

          <Step number={3} title={t('guide.step3Title')} img={IMG.download} imgAlt={t('guide.step3ImgAlt')} warning={t('guide.step3Warning')} zoomHint={zh}>
            <p>{t('guide.step3Body')}</p>
          </Step>

          <Step number={4} title={t('guide.step4Title')} img={IMG.files} imgAlt={t('guide.step4ImgAlt')} tip={t('guide.step4Tip')} zoomHint={zh}>
            <p>{t('guide.step4Body')}</p>
          </Step>

          <Step number={5} title={t('guide.step5Title')} img={IMG.dropzone} imgAlt={t('guide.step5ImgAlt')} tip={t('guide.step5Tip')} zoomHint={zh}>
            <p>{t('guide.step5Body1')}</p>
            <p>{t('guide.step5Body2')}</p>
          </Step>

          <Step number={6} title={t('guide.step6Title')} img={IMG.result} imgAlt={t('guide.step6ImgAlt')} zoomHint={zh}>
            <p>{t('guide.step6Body')}</p>
            <ul className="guide-list">
              <li><strong className="text-green">✓</strong> {t('guide.step6Li1').replace('✓ ', '')}</li>
              <li><strong className="text-yellow">H</strong> {t('guide.step6Li2').replace('H ', '')}</li>
              <li><strong className="text-muted">S</strong> {t('guide.step6Li3').replace('S ', '')}</li>
            </ul>
            <p>{t('guide.step6Body2')}</p>
          </Step>

          <Step number={7} title={t('guide.step7Title')} img={IMG.stats} imgAlt={t('guide.step7ImgAlt')} tip={t('guide.step7Tip')} zoomHint={zh}>
            <p>{t('guide.step7Body')}</p>
          </Step>
        </div>
      )}

      {/* ── ENTENDÉ TUS STATS ────────────────────────────────────────── */}
      {section === 'stats' && (
        <div className="guide-stats-section">
          <p className="guide-section-intro-text">{t('guide.statsIntro')}</p>

          <div className="stats-guide-grid">

            <div className="stats-guide-category">
              <h3 className="sgc-title">{t('guide.statsCatPreflop')}</h3>
              <div className="stats-guide-table">
                <div className="sgt-row header">
                  <span>{t('guide.statsColStat')}</span>
                  <span>{t('guide.statsColRange')}</span>
                  <span>{t('guide.statsColDesc')}</span>
                </div>
                <StatRow stat="VPIP"      range="20–27%" desc={t('guide.vpipDesc')} />
                <StatRow stat="PFR"       range="15–19%" desc={t('guide.pfrDesc')} />
                <StatRow stat="PFR/VPIP"  range=">70%"   desc={t('guide.pfrVpipDesc')} />
                <StatRow stat="3Bet"      range="8–12%"  desc={t('guide.threebetDesc')} />
                <StatRow stat="Fold to 3Bet" range="55–65%" desc={t('guide.fold3bDesc')} />
                <StatRow stat="ATS"       range="30–40%" desc={t('guide.atsDesc')} />
              </div>
            </div>

            <div className="stats-guide-category">
              <h3 className="sgc-title">{t('guide.statsCatCbet')}</h3>
              <div className="stats-guide-table">
                <div className="sgt-row header">
                  <span>{t('guide.statsColStat')}</span>
                  <span>{t('guide.statsColRange')}</span>
                  <span>{t('guide.statsColDesc')}</span>
                </div>
                <StatRow stat="CBet Flop"         range="55–70%" desc={t('guide.cbetFDesc')} />
                <StatRow stat="Double Barrel"     range="45–55%" desc={t('guide.cbetTDesc')} />
                <StatRow stat="Triple Barrel"     range="40–50%" desc={t('guide.cbetRDesc')} />
                <StatRow stat="CBet Flop Success" range="45–55%" desc={t('guide.cbetFSucDesc')} />
              </div>
            </div>

            <div className="stats-guide-category">
              <h3 className="sgc-title">{t('guide.statsCatDef')}</h3>
              <div className="stats-guide-table">
                <div className="sgt-row header">
                  <span>{t('guide.statsColStat')}</span>
                  <span>{t('guide.statsColRange')}</span>
                  <span>{t('guide.statsColDesc')}</span>
                </div>
                <StatRow stat="Fold to CBet Flop" range="35–50%" desc={t('guide.foldCbetFDesc')} />
                <StatRow stat="Fold to Double Barrel" range="35–50%" desc={t('guide.foldCbetTDesc')} />
                <StatRow stat="Probe Turn"        range="20–30%" desc={t('guide.probeDesc')} />
              </div>
            </div>

            <div className="stats-guide-category">
              <h3 className="sgc-title">{t('guide.statsCatSD')}</h3>
              <div className="stats-guide-table">
                <div className="sgt-row header">
                  <span>{t('guide.statsColStat')}</span>
                  <span>{t('guide.statsColRange')}</span>
                  <span>{t('guide.statsColDesc')}</span>
                </div>
                <StatRow stat="WTSD" range="30–35%" desc={t('guide.wtsdDesc')} />
                <StatRow stat="W@SD" range="53–58%" desc={t('guide.wsdDesc')} />
                <StatRow stat="WWSF" range="45–52%" desc={t('guide.wwsfDesc')} />
                <StatRow stat="AF"   range="2.0–3.0" desc={t('guide.afDesc')} />
              </div>
            </div>

          </div>

          <div className="guide-tip" style={{ marginTop: '1.5rem' }}>
            <span className="guide-tip-icon">💡</span>
            <span>{t('guide.statsTip')}</span>
          </div>
        </div>
      )}

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      {section === 'faq' && (
        <div className="guide-faq">
          {([
            ['faq1q', 'faq1a'],
            ['faq2q', 'faq2a'],
            ['faq3q', 'faq3a'],
            ['faq4q', 'faq4a'],
            ['faq5q', 'faq5a'],
            ['faq6q', 'faq6a'],
            ['faq7q', 'faq7a'],
            ['faq8q', 'faq8a'],
            ['faq9q', 'faq9a'],
            ['faq10q', 'faq10a'],
          ] as [string, string][]).map(([qKey, aKey]) => (
            <FaqItem key={qKey} q={t(`guide.${qKey}`)} a={t(`guide.${aKey}`)} />
          ))}
        </div>
      )}

      <style>{`
        .guide-view {
          max-width: 900px;
          margin: 0 auto;
          padding: 1.5rem 1rem 3rem;
          color: var(--text-primary, #e2e8f0);
        }

        /* Header */
        .guide-header { margin-bottom: 2rem; }
        .guide-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--accent, #00d4ff);
          margin: 0 0 0.4rem;
        }
        .guide-subtitle {
          color: var(--text-secondary, #94a3b8);
          margin: 0 0 1.2rem;
          font-size: 0.95rem;
        }
        .guide-tabs {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .guide-tab {
          background: var(--bg-card, #1e293b);
          border: 1px solid var(--border, #334155);
          color: var(--text-secondary, #94a3b8);
          padding: 0.45rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s;
        }
        .guide-tab:hover { border-color: var(--accent, #00d4ff); color: var(--text-primary, #e2e8f0); }
        .guide-tab.active {
          background: var(--accent, #00d4ff);
          border-color: var(--accent, #00d4ff);
          color: #0f172a;
          font-weight: 600;
        }

        /* Section intro */
        .guide-section-intro {
          background: var(--bg-card, #1e293b);
          border: 1px solid var(--border, #334155);
          border-radius: 8px;
          padding: 1rem 1.25rem;
          margin-bottom: 1.75rem;
          font-size: 0.9rem;
          line-height: 1.6;
          color: var(--text-secondary, #94a3b8);
        }
        .guide-section-intro p { margin: 0; }

        /* Steps */
        .guide-steps { display: flex; flex-direction: column; gap: 1.5rem; }

        .guide-step {
          background: var(--bg-card, #1e293b);
          border: 1px solid var(--border, #334155);
          border-radius: 10px;
          overflow: hidden;
        }
        .guide-step-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.9rem 1.25rem;
          background: var(--bg-secondary, #0f172a);
          border-bottom: 1px solid var(--border, #334155);
        }
        .guide-step-num {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: var(--accent, #00d4ff);
          color: #0f172a;
          font-weight: 700;
          font-size: 0.875rem;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .guide-step-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary, #e2e8f0);
        }
        .guide-step-body {
          display: flex;
          gap: 1.5rem;
          padding: 1.25rem;
          align-items: flex-start;
        }
        .guide-step-text {
          flex: 1;
          font-size: 0.9rem;
          line-height: 1.65;
          color: var(--text-secondary, #94a3b8);
        }
        .guide-step-text p { margin: 0 0 0.75rem; }
        .guide-step-text p:last-child { margin-bottom: 0; }
        .guide-step-text strong, .guide-step-text em { color: var(--text-primary, #e2e8f0); }

        .guide-list {
          margin: 0.5rem 0;
          padding-left: 1.25rem;
          line-height: 1.9;
        }
        .guide-list li strong { color: var(--text-primary, #e2e8f0); }

        .guide-step-img-wrap { flex-shrink: 0; width: 280px; }
        .guide-step-img {
          width: 100%;
          border-radius: 6px;
          border: 1px solid var(--border, #334155);
          cursor: zoom-in;
          transition: transform 0.2s;
        }
        .guide-img-zoom {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%,-50%) scale(1) !important;
          width: min(90vw, 900px);
          z-index: 9999;
          cursor: zoom-out;
          box-shadow: 0 0 0 100vw rgba(0,0,0,0.8);
        }
        .guide-img-caption {
          text-align: center;
          font-size: 0.72rem;
          color: var(--text-muted, #64748b);
          margin-top: 0.4rem;
        }

        /* Tip / Warning */
        .guide-tip, .guide-warning {
          display: flex;
          gap: 0.5rem;
          align-items: flex-start;
          padding: 0.6rem 0.85rem;
          border-radius: 6px;
          font-size: 0.835rem;
          margin-top: 0.75rem;
          line-height: 1.5;
        }
        .guide-tip {
          background: rgba(0, 212, 255, 0.08);
          border: 1px solid rgba(0, 212, 255, 0.2);
          color: var(--text-secondary, #94a3b8);
        }
        .guide-warning {
          background: rgba(251, 191, 36, 0.08);
          border: 1px solid rgba(251, 191, 36, 0.25);
          color: #fbbf24;
        }
        .guide-tip-icon { flex-shrink: 0; }

        /* Stats guide */
        .guide-section-intro-text {
          color: var(--text-secondary, #94a3b8);
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
        }
        .stats-guide-grid { display: flex; flex-direction: column; gap: 1.5rem; }
        .stats-guide-category {
          background: var(--bg-card, #1e293b);
          border: 1px solid var(--border, #334155);
          border-radius: 10px;
          overflow: hidden;
        }
        .sgc-title {
          margin: 0;
          padding: 0.75rem 1.25rem;
          font-size: 0.95rem;
          font-weight: 600;
          background: var(--bg-secondary, #0f172a);
          border-bottom: 1px solid var(--border, #334155);
          color: var(--text-primary, #e2e8f0);
        }
        .stats-guide-table { padding: 0.25rem 0; }
        .sgt-row {
          display: grid;
          grid-template-columns: 140px 120px 1fr;
          gap: 0.75rem;
          padding: 0.6rem 1.25rem;
          font-size: 0.835rem;
          border-bottom: 1px solid rgba(51,65,85,0.5);
          align-items: baseline;
        }
        .sgt-row:last-child { border-bottom: none; }
        .sgt-row.header {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-muted, #64748b);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding-top: 0.5rem;
        }
        .sgt-stat { font-weight: 600; color: var(--accent, #00d4ff); font-size: 0.875rem; }
        .sgt-range { font-weight: 600; }
        .sgt-range.opt { color: #22c55e; }
        .sgt-row > span:last-child { color: var(--text-secondary, #94a3b8); line-height: 1.55; }

        /* Text colors */
        .text-green { color: #22c55e; }
        .text-yellow { color: #eab308; }
        .text-muted { color: var(--text-muted, #64748b); }

        /* FAQ */
        .guide-faq { display: flex; flex-direction: column; gap: 0.5rem; }
        .faq-item {
          background: var(--bg-card, #1e293b);
          border: 1px solid var(--border, #334155);
          border-radius: 8px;
          overflow: hidden;
          transition: border-color 0.15s;
        }
        .faq-item.faq-open { border-color: var(--accent, #00d4ff); }
        .faq-q {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 0.9rem 1.25rem;
          background: none;
          border: none;
          color: var(--text-primary, #e2e8f0);
          font-size: 0.9rem;
          font-weight: 500;
          text-align: left;
          cursor: pointer;
          transition: color 0.15s;
        }
        .faq-q:hover { color: var(--accent, #00d4ff); }
        .faq-arrow { flex-shrink: 0; font-size: 0.75rem; color: var(--text-muted, #64748b); }
        .faq-a {
          padding: 0.9rem 1.25rem 1rem;
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--text-secondary, #94a3b8);
          border-top: 1px solid var(--border, #334155);
        }
        .faq-a p { margin: 0; }

        @media (max-width: 640px) {
          .guide-step-body { flex-direction: column; }
          .guide-step-img-wrap { width: 100%; }
          .sgt-row { grid-template-columns: 110px 90px 1fr; gap: 0.5rem; font-size: 0.78rem; }
        }
      `}</style>
    </div>
  );
}
